import './style.scss';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import React from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import { isMobile } from 'react-device-detect';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { Button } from 'reactstrap';

export type ModalDialogType =
  | 'default'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'error'
  | 'input'
  | 'custom'
  | 'controlled';

interface ModalDialogProps {
  show: boolean;
  title: string;
  message: string;
  type?: ModalDialogType;
  onConfirm?: () => void;
  onClose?: () => void;
  buttons?: {
    label: string;
    color?: string;
    onClick: () => void;
    style?: React.CSSProperties;
  }[];
}

export interface ModalDialogComponent extends React.FC<ModalDialogProps> {
  show: (props: Omit<ModalDialogProps, 'show'>) => void;
  close: () => void;
}

const ModalDialog: ModalDialogComponent = (props: ModalDialogProps) => {
  if (isMobile) {
    const actions = props.buttons
      ? props.buttons.map((button, index) => (
          <Button key={index} onClick={button.onClick}>
            {button.label}
          </Button>
        ))
      : [
          <Button key="dismiss" onClick={props.onClose}>
            Dismiss
          </Button>,
        ];
    return (
      <Dialog open={props.show} onClose={props.onClose}>
        <DialogTitle id="alert-dialog-title">{props.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {props.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    );
  }
  return (
    <SweetAlert
      show={props.show}
      title={props.title}
      type={props.type || 'info'}
      showCancelButton
      onConfirm={props.onConfirm}
      onCancel={props.onClose}
      customButtons={
        props.buttons && (
          <>
            {props.buttons.map((button, index) => (
              <Button
                key={index}
                color={button.color || 'light'}
                style={{ margin: 5, ...(button.style || {}) }}
                onClick={button.onClick}
              >
                {button.label}
              </Button>
            ))}
          </>
        )
      }
      onOutsideClick={props.onClose}
    >
      {props.message}
    </SweetAlert>
  );
};

// For programmatically showing/hiding the modal, we store the root
let modalRoot: Root | null = null;

// "Show" static function – creates a container if needed and renders the component.
ModalDialog.show = (props: Omit<ModalDialogProps, 'show'>) => {
  let divTarget = document.getElementById('react-modal-dialog');
  if (!divTarget) {
    divTarget = document.createElement('div');
    divTarget.id = 'react-modal-dialog';
    document.body.appendChild(divTarget);
    modalRoot = createRoot(divTarget);
  } else if (!modalRoot) {
    modalRoot = createRoot(divTarget);
  }
  modalRoot.render(<ModalDialog show {...props} />);
};

// "Close" static function – unmounts the component and removes the container.
ModalDialog.close = () => {
  const target = document.getElementById('react-modal-dialog');
  if (target && modalRoot) {
    modalRoot.unmount();
    target.parentNode?.removeChild(target);
    modalRoot = null;
  }
};

export default ModalDialog;
